// SchadenlistenMenue.tsx — Knopf „Schadenlisten" in der Kopfleiste von /damages.
//
// Die Schadenlisten sind Excel-Mappen je Kunde (User 05.10.2026: „ein Button
// mit Dropdown für die jeweilige Schadenliste"), die bisher auf dem Laufwerk
// lagen. Hier hängen sie als gewöhnliche Dokumente mit der Kategorie
// „Schadenliste" und OHNE Kunden-/Schadenbezug — die Liste gehört dem Büro,
// nicht einer Akte. Ein Klick öffnet dieselbe Auswahl „Im Web / Desktop" wie
// jede andere Office-Datei; gespeichert wird als neue Fassung.
//
// Eigenes Popover im Muster von `neuAnlage/NeuMenue.tsx` (kein Fluent-Popup).

import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { Add20Regular, ChevronDown16Regular, Table20Regular } from "@fluentui/react-icons";
import { documentService, type DocumentDto } from "../../services/documentService";
import { useOfficeOpenMenu } from "../documents/OfficeOpenMenuProvider";
import { KATEGORIE_SCHADENLISTE, listenName, sortiereListen } from "./schadenlisten";
import "../neuAnlage/neuAnlage.css";
import { alleFenster, aktivesDokument, aktivesFenster } from "../windowManager/browserFenster/fensterRegister";

const BREITE = 340;
const RAND = 12;

export function SchadenlistenMenue() {
  // Bewusst OHNE Rechteprüfung im Frontend (06.10.2026): `documents:read`/
  // `documents:write` sind Basis-Scopes jedes eingeloggten Nutzers, stehen aber
  // nicht in `/rbac/me/permissions` — eine Prüfung darauf blendete den Knopf
  // für alle `mitarbeiter`/`service` aus. Die Absicherung liegt im Backend.
  const oeffneOffice = useOfficeOpenMenu();

  const [offen, setOffen] = React.useState(false);
  const [pos, setPos] = React.useState<{ top: number; left: number; width: number } | null>(null);
  const [listen, setListen] = React.useState<DocumentDto[] | null>(null);
  const [hinweis, setHinweis] = React.useState<string | null>(null);
  const [laedtHoch, setLaedtHoch] = React.useState(false);
  const knopfRef = React.useRef<HTMLButtonElement>(null);
  const menueRef = React.useRef<HTMLDivElement>(null);
  const dateiRef = React.useRef<HTMLInputElement>(null);

  const platzieren = React.useCallback(() => {
    const el = knopfRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = aktivesFenster().innerWidth;
    const width = Math.min(BREITE, vw - 2 * RAND);
    setPos({ top: r.bottom + 8, left: Math.max(RAND, Math.min(r.left, vw - width - RAND)), width });
  }, []);

  const laden = React.useCallback(async () => {
    try {
      setListen(sortiereListen(await documentService.list({ category: KATEGORIE_SCHADENLISTE })));
    } catch {
      setListen([]);
      setHinweis("Die Schadenlisten konnten nicht geladen werden.");
    }
  }, []);

  const schliessen = React.useCallback((fokusZurueck = true) => {
    setOffen(false);
    if (fokusZurueck) knopfRef.current?.focus();
  }, []);

  const umschalten = () => {
    if (offen) {
      schliessen();
      return;
    }
    setHinweis(null);
    platzieren();
    setOffen(true);
    void laden();
  };

  React.useEffect(() => {
    if (!offen) return;
    const beiKlick = (e: PointerEvent) => {
      const t = e.target as Node;
      if (menueRef.current?.contains(t) || knopfRef.current?.contains(t)) return;
      schliessen(false);
    };
    const beiTaste = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        schliessen();
      }
    };
    alleFenster.addEventListener("pointerdown", beiKlick);
    alleFenster.addEventListener("keydown", beiTaste);
    alleFenster.addEventListener("resize", platzieren);
    return () => {
      alleFenster.removeEventListener("pointerdown", beiKlick);
      alleFenster.removeEventListener("keydown", beiTaste);
      alleFenster.removeEventListener("resize", platzieren);
    };
  }, [offen, schliessen, platzieren]);

  // Pfeiltasten wandern ringförmig durch alle Einträge des Menüs.
  const beiPfeil = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const eintraege = Array.from(menueRef.current?.querySelectorAll<HTMLButtonElement>("[role=menuitem]") ?? []);
    if (!eintraege.length) return;
    const i = eintraege.indexOf(aktivesDokument().activeElement as HTMLButtonElement);
    let ziel: number | null = null;
    if (e.key === "ArrowDown") ziel = (i + 1) % eintraege.length;
    else if (e.key === "ArrowUp") ziel = (i - 1 + eintraege.length) % eintraege.length;
    else if (e.key === "Home") ziel = 0;
    else if (e.key === "End") ziel = eintraege.length - 1;
    else if (e.key === "Tab") schliessen(false);
    if (ziel === null) return;
    e.preventDefault();
    eintraege[ziel]?.focus();
  };

  const hochladen = async (dateien: File[]) => {
    if (!dateien.length) return;
    setLaedtHoch(true);
    setHinweis(null);
    const fehler: string[] = [];
    for (const file of dateien) {
      try {
        await documentService.upload({ file, category: KATEGORIE_SCHADENLISTE });
      } catch (err) {
        fehler.push(`„${file.name}“: ${err instanceof Error ? err.message : "Hochladen fehlgeschlagen."}`);
      }
    }
    await laden();
    setLaedtHoch(false);
    if (fehler.length) setHinweis(fehler.join(" "));
  };

  return (
    <>
      <button
        ref={knopfRef}
        type="button"
        className="tbtn"
        aria-haspopup="menu"
        aria-expanded={offen}
        onClick={umschalten}
      >
        <Table20Regular />
        Schadenlisten
        <ChevronDown16Regular />
      </button>
      {offen && pos
        ? createPortal(
            <div
              ref={menueRef}
              className="na-menu"
              role="menu"
              aria-label="Schadenlisten"
              style={{ top: pos.top, left: pos.left, width: pos.width }}
              onKeyDown={beiPfeil}
            >
              <div className="na-menu-kopf" aria-hidden>
                Schadenlisten
              </div>
              <div className="na-menu-liste">
                {listen === null ? (
                  <p role="status" style={{ margin: 0, padding: "8px 10px", fontSize: 14 }}>
                    Lade Schadenlisten …
                  </p>
                ) : listen.length === 0 ? (
                  <p style={{ margin: 0, padding: "8px 10px", fontSize: 14 }}>
                    Noch keine Schadenliste hinterlegt.
                  </p>
                ) : (
                  listen.map((liste) => (
                    <button
                      key={liste.id}
                      type="button"
                      role="menuitem"
                      className="na-menu-eintrag"
                      title={liste.fileName}
                      onClick={() => {
                        schliessen(false);
                        oeffneOffice(
                          { id: liste.id, fileName: liste.fileName, mimeType: liste.mimeType },
                          knopfRef.current ?? undefined,
                        );
                      }}
                    >
                      <span className="na-menu-ico" aria-hidden>
                        <Table20Regular />
                      </span>
                      {listenName(liste.fileName)}
                    </button>
                  ))
                )}
                <button
                  type="button"
                  role="menuitem"
                  className="na-menu-eintrag"
                  disabled={laedtHoch}
                  style={{ marginTop: 6, borderTop: "1px solid #ece6ec", borderRadius: "0 0 9px 9px" }}
                  onClick={() => dateiRef.current?.click()}
                >
                  <span className="na-menu-ico" aria-hidden>
                    <Add20Regular />
                  </span>
                  {laedtHoch ? "Wird hochgeladen …" : "Liste hinzufügen …"}
                </button>
              </div>
              {hinweis && (
                <p role="alert" style={{ margin: 0, padding: "8px 10px 4px", fontSize: 13, color: "#a4262c" }}>
                  {hinweis}
                </p>
              )}
              <input
                ref={dateiRef}
                type="file"
                accept=".xlsx,.xlsm"
                multiple
                hidden
                onChange={(e) => {
                  const dateien = Array.from(e.target.files ?? []);
                  e.target.value = "";
                  void hochladen(dateien);
                }}
              />
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

export default SchadenlistenMenue;
